import { PromiseActorLogic, fromPromise } from 'xstate';

import { TsRestClient } from '@eluve/api-client-provider';
import { PlatformTypesEnum } from '@eluve/graphql-types';

export type PrepareAppointmentAudioRecordsResponse = {
  resumableUploadSessionUri?: string;
  nextChunkResumableUploadSessionUri?: string;
};

export type PrepareAppointmentAudioRecordsInput = {
  tenantId: string;
  appointmentId?: string;
  fileName: string;
  useResumableUploads: boolean;
  platform?: PlatformTypesEnum;
  platformId?: string;
  platformVersion?: string;
  userAgent?: string;
  microphoneName?: string;
  timeout?: number;
};

export const createPrepareAppointmentAudioRecordsActor = ({
  apiClient,
  clientOrigin,
  platform,
  platformId,
  platformVersion,
  defaultTimeout = 3000,
}: {
  apiClient: TsRestClient | undefined | null;
  clientOrigin?: string;
  platform?: PlatformTypesEnum;
  platformId?: string;
  platformVersion?: string;
  defaultTimeout?: number;
}): PromiseActorLogic<
  PrepareAppointmentAudioRecordsResponse,
  PrepareAppointmentAudioRecordsInput
> => {
  return fromPromise(
    async ({
      input: {
        fileName,
        tenantId,
        appointmentId,
        useResumableUploads,
        userAgent,
        microphoneName,
        timeout,
      },
    }) => {
      if (!appointmentId) {
        // In the future we could use this to support non-appointment recordings
        return {
          resumableUploadSessionUri: undefined,
        };
      }

      const timeoutMs = timeout ?? defaultTimeout;
      let timeoutId: NodeJS.Timeout | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(
            new Error(
              `prepareAppointmentAudioRecords timed out after ${timeoutMs}ms`,
            ),
          );
        }, timeoutMs);
      });

      const apiCallPromise =
        apiClient?.appointments.prepareAppointmentAudioRecords({
          params: {
            tenantId,
            appointmentId,
          },
          body: {
            fileName,
            useResumableUploads,
            origin: clientOrigin,
            platform,
            platformId,
            platformVersion,
            userAgent,
            microphoneName,
          },
        });

      const response = await Promise.race([apiCallPromise, timeoutPromise]);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (response?.status !== 200) {
        throw new Error(
          `Failed to prepare appointment audio records. Response code: ${response?.status}`,
        );
      }

      const { resumableUploadSessionUri, nextChunkResumableUploadSessionUri } =
        response.body;

      return {
        resumableUploadSessionUri,
        nextChunkResumableUploadSessionUri,
      };
    },
  );
};
